<script lang="ts">
	import type { Mediator, SvelteAsr } from 'types/common'

	import { ChartConfiguration } from '@isoftdata/svelte-dashboard'
	import { hasPermission } from 'utility/permission'
	import { getContext, type ComponentProps } from 'svelte'
	import { graphql } from '$houdini'

	interface Props {
		asr: SvelteAsr
		charts: ComponentProps<typeof ChartConfiguration>['charts']
		defaultParameters: ComponentProps<typeof ChartConfiguration>['defaultParameters']
		reports: ComponentProps<typeof ChartConfiguration>['reports']
	}

	let {
		//
		asr,
		charts,
		defaultParameters,
		reports,
	}: Props = $props()

	const mediator = getContext<Mediator>('mediator')

	const createChartMutation = graphql(`
		mutation CreateDashboardChart($input: NewDashboardChartInput!) {
			createDashboardChart(input: $input) {
				id
			}
		}
	`)

	const updateChartMutation = graphql(`
		mutation UpdateDashboardChart($input: UpdateDashboardChartInput!) {
			updateDashboardChart(input: $input) {
				id
			}
		}
	`)

	const loadArbitraryChartData = graphql(`
		query LoadArbitraryChartData($input: LoadArbitraryChartDataInput!) {
			loadArbitraryChartData(input: $input)
		}
	`)
</script>

<ChartConfiguration
	{charts}
	{reports}
	{defaultParameters}
	canEditQuery={hasPermission('DASHBOARD_CHART_EDIT_QUERY')}
	chartChange={({ chartId }) => asr.go(null, { selectedChartId: chartId ?? null }, { inherit: true })}
	confirm={() => asr.go(null, { lastSaveTime: Date.now() }, { inherit: true })}
	loadChartData={async (chart, parameters) => {
		const { data } = await loadArbitraryChartData.fetch({
			variables: {
				input: {
					chart: JSON.stringify(chart),
					parameterSelectionList: parameters ?? [],
				},
			},
		})

		return JSON.parse(data?.loadArbitraryChartData ?? '{}')
	}}
	showError={(heading, message) => {
		mediator.publish('showMessage', {
			heading,
			message,
			color: 'danger',
			time: false,
		})
	}}
	createChart={async chart => {
		await createChartMutation.mutate({
			input: {
				dataSourceName: chart.dataSourceName,
				jsonString: chart.jsonString,
				name: chart.name,
				showChart: true,
				supertype: chart.supertype.toUpperCase() as 'EMBED' | 'GOOGLE' | 'TABLE',
				title: chart.title,
				description: '',
			},
		})
	}}
	updateChart={async chart => {
		await updateChartMutation.mutate({
			input: {
				id: chart.id,
				jsonString: chart.jsonString,
				name: chart.name,
				showChart: true,
				title: chart.title,
				description: '',
			},
		})
	}}
></ChartConfiguration>
